import React, { useState } from 'react';
import { Box } from '@mui/system';
import {
  Button,
  Container,
  Typography,
  CircularProgress,
  Dialog,
  DialogContentText,
  Grid,
  Divider,
  InputAdornment,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { DocumentData } from '@google-cloud/firestore';
import { FormTextField } from '../custom/CustomComponents';
import { Print } from '@mui/icons-material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { submitBudgetForm } from '../../services';

interface TableRow {
  description: string;
  date: string;
  amount: number;
}

interface OtherRequest {
  purpose: string;
  amount: number;
  date: string;
}

export interface IBudgetFormData {
  submissionDate: string;
  ministryName: string;
  ministryLeader: string;
  cellContact: string;
  email: string;
  rows: TableRow[];
  otherRequest: OtherRequest;
  comments: string;
  signature: string;
  signatureDate: string;
  committeeComments: string;
  contactMinistryLeader: string;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sumOfArray(numbers: number[]): number {
  let sum = 0;
  for (let i = 0; i < numbers.length; i++) {
    sum += numbers[i];
  }
  return sum;
}

const BudgetRequestForm = ({ form = null }: { form?: DocumentData | null }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState<IBudgetFormData>({
    submissionDate: form?.submissionDate ?? formatDate(new Date()),
    ministryName: form?.ministryName ?? '',
    ministryLeader: form?.ministryLeader ?? '',
    cellContact: form?.cellContact ?? '',
    email: form?.email ?? '',
    rows: form?.rows ?? [{ description: '', date: '', amount: '0' }],
    otherRequest: form?.otherRequest ?? { purpose: '', amount: 0, date: '' },
    comments: form?.comments ?? '',
    signature: form?.signature ?? '',
    signatureDate: form?.signatureDate ?? formatDate(new Date()),
    committeeComments: form?.committeeComments ?? '',
    contactMinistryLeader: form?.contactMinistryLeader ?? '',
  });

  const handleAddRow = () => {
    setFormData({
      ...formData,
      rows: [...formData.rows, { description: '', date: '', amount: 0 }],
    });
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex?: number
  ) => {
    const { name, value } = event.target;
    const rows = [...formData.rows];
    if (rowIndex !== undefined && rowIndex !== null)
      rows[rowIndex] = { ...rows[rowIndex], [name]: value };
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
      rows: [...rows],
    });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleOtherRequestChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      otherRequest: { ...formData.otherRequest, [name]: value },
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    submitBudgetForm(formData)
      .then(() => {
        setShowModal(true);
      })
      .catch(() => {
        alert('Error Submitting');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const { ministryName, ministryLeader, email, signature, signatureDate } =
    formData;
  const isDisabled = !(
    ministryName &&
    ministryLeader &&
    email &&
    signature &&
    signatureDate
  );

  const formDisabled = form !== null;

  const print = () => {
    window.print();
  };
  const removeRow = (index: number) => {
    const rows = formData.rows;
    rows.splice(index, 1);
    setFormData({
      ...formData,
      rows,
    });
  };

  const totalAmountRequested = sumOfArray([
    ...formData.rows.map((x) => Number(x.amount)),
    Number(formData.otherRequest.amount),
  ]).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return (
    <Box sx={{ margin: '2rem' }}>
      <Container sx={{ width: { md: '50%', xs: '100%' } }}>
        <Typography variant="h5" align="center" gutterBottom>
          2027 BUDGET PLANNING REQUEST FORM
        </Typography>
        {!formDisabled && (
          <Typography align="center" gutterBottom>
            <a
              href="/budget-planning-letter"
              target="_blank"
              style={{ textAlign: 'center' }}
            >
              2027 Budget Planning Letter
            </a>
          </Typography>
        )}
        {formDisabled && (
          <div className="no-print">
            <Button variant="contained" startIcon={<Print />} onClick={print}>
              Print Completed Form
            </Button>
          </div>
        )}

        <Typography
          variant="body1"
          align="center"
          paragraph
          style={{ color: 'red' }}
        >
          Deadline for Submission: October 6, 2026
        </Typography>

        <form onSubmit={handleSubmit}>
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Submission Date"
            name="submissionDate"
            type="date"
            value={formData.submissionDate}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Typography sx={{ mt: 2, fontWeight: 'bold' }} variant="h6">
            SECTION ONE: MINISTRY INFORMATION
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Ministry"
            name="ministryName"
            value={formData.ministryName}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Name of Ministry Leader"
            name="ministryLeader"
            value={formData.ministryLeader}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Cell"
            name="cellContact"
            value={formData.cellContact}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
          />

          <Typography sx={{ mt: 3, fontWeight: 'bold' }} variant="h6">
            SECTION TWO: PLEASE LIST 2027 MINISTRY NEEDS, GOALS & EVENTS
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <Grid container spacing={2}>
            {formData.rows.map((row, index) => (
              <React.Fragment key={index}>
                <Grid item xs={index === 0 ? 12 : 11}>
                  <FormTextField
                    disabled={formDisabled}
                    name="description"
                    label="Ministry Needs/Goals/Events"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={row.description}
                    onChange={(event) => handleChange(event, index)}
                  />
                </Grid>
                {index !== 0 && (
                  <Grid item xs={1}>
                    <Button onClick={() => removeRow(index)}>Remove</Button>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <FormTextField
                    disabled={formDisabled}
                    name="amount"
                    label="Amount Requested"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={row.amount}
                    onChange={(event) => handleChange(event, index)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormTextField
                    disabled={formDisabled}
                    name="date"
                    label="Date Funds Are Needed"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={row.date}
                    onChange={(event) => handleChange(event, index)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ margin: 1 }} />
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          <Button color="primary" onClick={handleAddRow}>
            Add More
          </Button>

          <Typography sx={{ mt: 3, fontWeight: 'bold' }}>
            Other Request
          </Typography>
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Purpose"
            name="purpose"
            value={formData.otherRequest.purpose}
            onChange={handleOtherRequestChange}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormTextField
                disabled={formDisabled}
                name="amount"
                label="Amount Requested"
                type="number"
                variant="outlined"
                fullWidth
                value={formData.otherRequest.amount}
                onChange={handleOtherRequestChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormTextField
                disabled={formDisabled}
                name="date"
                label="Date Funds Are Needed"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={formData.otherRequest.date}
                onChange={handleOtherRequestChange}
              />
            </Grid>
          </Grid>

          <Typography sx={{ mt: 2, fontWeight: 'bold', color: 'red' }}>
            TOTAL AMOUNT REQUESTED: {totalAmountRequested}
          </Typography>

          <Typography sx={{ mt: 3, fontWeight: 'bold' }} variant="h6">
            SECTION THREE: ADDITIONAL INFORMATION
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Additional Information"
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            multiline
            rows={4}
          />

          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Ministry Leader Signature"
            name="signature"
            value={formData.signature}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Date"
            name="signatureDate"
            type="date"
            value={formData.signatureDate}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Typography sx={{ mt: 3, fontWeight: 'bold' }} variant="h6">
            BUDGET COMMITTEE COMMENTS
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Budget Committee Comments"
            name="committeeComments"
            value={formData.committeeComments}
            onChange={handleChange}
            multiline
            rows={3}
          />
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">
              Contact the Ministry leader for additional information?
            </FormLabel>
            <RadioGroup
              row
              name="contactMinistryLeader"
              value={formData.contactMinistryLeader}
              onChange={handleRadioChange}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio />}
                label="Yes"
                disabled={formDisabled}
              />
              <FormControlLabel
                value="No"
                control={<Radio />}
                label="No"
                disabled={formDisabled}
              />
            </RadioGroup>
          </FormControl>

          {!form && (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginTop: '1em', display: 'block' }}
              disabled={isDisabled || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          )}

          <Dialog
            open={showModal}
            onClose={() => {}}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Form Submitted</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Form submitted. You will be contacted shortly
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </form>
        <Typography variant="body1" align="center" paragraph sx={{ mt: 2 }}>
          Thank you for your timely submission of your budget request form.
          Please contact the church office with questions.
        </Typography>
      </Container>
    </Box>
  );
};

export default BudgetRequestForm;
